import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import { isDraftStandardObject } from '../../utils/standardObjectPublishing';

const DEFAULT_CATALOG = {
  allow_explore: true,
  create_fields: true,
  assign_permission: true,
  setup_object: false,
  package_required: true,
};

const PublishStandardObjectModal = ({ show, onHide, object, onSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [packageKey, setPackageKey] = useState('');
  const [allowExplore, setAllowExplore] = useState(DEFAULT_CATALOG.allow_explore);
  const [createFields, setCreateFields] = useState(DEFAULT_CATALOG.create_fields);
  const [assignPermission, setAssignPermission] = useState(DEFAULT_CATALOG.assign_permission);
  const [setupObject, setSetupObject] = useState(DEFAULT_CATALOG.setup_object);
  const [packageRequired, setPackageRequired] = useState(DEFAULT_CATALOG.package_required);
  const [loading, setLoading] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [error, setError] = useState('');

  const isPublishMode = isDraftStandardObject(object);

  useEffect(() => {
    if (!show || !object?.object_name) {
      return undefined;
    }

    let cancelled = false;
    setError('');
    setLoadingCatalog(true);

    ObjectConfigApi.fetchPublishCatalog(object.object_name)
      .then((res) => {
        if (cancelled) return;
        if (res?.success) {
          const pkgs = res.data?.packages || [];
          const defaults = res.data?.defaultCatalogConfig || DEFAULT_CATALOG;
          const cfg = res.data?.catalogConfig || {};
          setPackages(pkgs);
          setAllowExplore(cfg.allow_explore !== undefined
            ? cfg.allow_explore !== false
            : defaults.allow_explore !== false);
          setCreateFields(cfg.create_fields !== undefined
            ? cfg.create_fields !== false
            : defaults.create_fields !== false);
          setAssignPermission(cfg.assign_permission !== undefined
            ? cfg.assign_permission !== false
            : defaults.assign_permission !== false);
          setSetupObject(cfg.setup_object === true);
          setPackageRequired(cfg.package_required !== undefined
            ? cfg.package_required !== false
            : defaults.package_required !== false);
          setPackageKey(cfg.package ? String(cfg.package) : '');
        } else {
          setError(res?.message || 'Failed to load catalog settings');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load catalog settings');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCatalog(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, object?.object_name]);

  const buildPayload = () => ({
    packageKey,
    allowExplore,
    createFields,
    assignPermission,
    setupObject,
    packageRequired,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!object?.object_name) return;

    setLoading(true);
    setError('');

    try {
      const payload = buildPayload();
      const res = isPublishMode
        ? await ObjectConfigApi.publishStandardObject({
          objectName: object.object_name,
          ...payload,
        })
        : await ObjectConfigApi.updateStandardObjectCatalog(object.object_name, payload);

      if (res?.success) {
        onSuccess?.(res.data);
        onHide();
      } else {
        setError(res?.message || 'Failed to save catalog settings');
      }
    } catch (err) {
      setError(err?.message || 'Failed to save catalog settings');
    } finally {
      setLoading(false);
    }
  };

  const objectLabel = object?.object_label || object?.object_name;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isPublishMode ? 'Publish Standard Object' : 'Edit Catalog Settings'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <p className="text-muted mb-3">
            {isPublishMode ? (
              <>
                Publish <strong>{objectLabel}</strong> to the standard object catalog.
                Tab settings are managed separately in Tab Manager.
              </>
            ) : (
              <>
                Update <code>public.standard_object</code> catalog settings for{' '}
                <strong>{objectLabel}</strong>. Edit tabs in Tab Manager.
              </>
            )}
          </p>
          {loadingCatalog ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading settings...
            </div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Package</Form.Label>
                <Form.Select
                  value={packageKey}
                  onChange={(e) => setPackageKey(e.target.value)}
                >
                  <option value="">— No package —</option>
                  {packages.map((pkg) => (
                    <option key={pkg.package_key} value={pkg.package_key}>
                      {pkg.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Assign this standard object to a product package.
                </Form.Text>
              </Form.Group>

              <div className="mb-0">
                <Form.Label className="mb-2">Object catalog settings</Form.Label>
                <Form.Check
                  type="checkbox"
                  id="publish-allow-explore"
                  className="mb-2"
                  label="Allow Explore"
                  checked={allowExplore}
                  onChange={(e) => setAllowExplore(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="publish-create-fields"
                  className="mb-2"
                  label="Allow Create Fields"
                  checked={createFields}
                  onChange={(e) => setCreateFields(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="publish-assign-permission"
                  className="mb-2"
                  label="Allow Additional Permission"
                  checked={assignPermission}
                  onChange={(e) => setAssignPermission(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="publish-setup-object"
                  className="mb-2"
                  label="Setup Object"
                  checked={setupObject}
                  onChange={(e) => setSetupObject(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  id="publish-package-required"
                  label="Package Required"
                  checked={packageRequired}
                  onChange={(e) => setPackageRequired(e.target.checked)}
                />
                <Form.Text className="text-muted d-block mt-1">
                  Uncheck to mark as optional (package_required: false) — not auto-added when a package is enabled.
                </Form.Text>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={isPublishMode ? 'success' : 'primary'}
            type="submit"
            disabled={loading || loadingCatalog}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              isPublishMode ? 'Publish Standard Object' : 'Save Catalog Settings'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PublishStandardObjectModal;
